/** Shared logged-out copy across Riftbound DB. */

// ── Global ───────────────────────────────────────────────────────────────────

export const GUEST_SIGN_IN_BUTTON = 'Sign in / Register';

/** Compact label for tight spaces (header, inline buttons). */
export const GUEST_SIGN_IN_BUTTON_SHORT = 'Sign in';

/** Auth cover / dialog heading + supporting line. */
export const SIGN_IN_TITLE = 'Welcome to Riftbound DB';
export const SIGN_IN_BLURB =
  'Your collection, decks, and match history — synced on every device.';

export const GUEST_SYNC_HINT =
  'Sign in when you\'re ready to sync your collection, decks, and progress across devices.';

export function guestSignInToastTitle(action: string): string {
  return `Sign in to ${action}`;
}

export const GUEST_FEATURES_LINK_LABEL = 'About collection, scanner & binders →';
export const GUEST_FEATURES_LINK_PATH = '/features/collection';

export const GUEST_CONTINUE_AS_GUEST = 'Continue as guest';

// ── Profile ──────────────────────────────────────────────────────────────────

export const GUEST_PROFILE_TITLE = 'Sign in to Riftbound DB';
export const GUEST_PROFILE_BLURB =
  'Track your collection, build decks, save match history, and manage your profile.';

// ── Collection ───────────────────────────────────────────────────────────────

export const GUEST_COLLECTION_BLURB =
  'Track copies, spot missing playsets, and export your collection without a spreadsheet.';

export const GUEST_BROWSE_CARDS_BUTTON = 'Browse cards';

export const GUEST_COLLECTION_RARITY_CAPTION =
  'Your cards by rarity will appear here as you add copies.';

export const GUEST_CARD_COLLECTION_BLURB =
  'Sign in to track this card in your collection.';

// ── Binders ──────────────────────────────────────────────────────────────────

/** Guest page title — pairs with GUEST_BINDERS_BLURB (mirrors collection tracker pattern). */
export const GUEST_BINDERS_PAGE_TITLE = 'Riftbound Virtual Binder Shelf';

export const GUEST_BINDERS_BLURB =
  'Every released set on one shelf. Open a binder to flip through cards in collector order.';

export const GUEST_EXPLORE_BINDERS_BUTTON = 'Explore binders';

export const GUEST_BINDER_SHELF_SCROLL_ID = 'binder-shelf';

export const GUEST_BINDER_GATE_TITLE = 'Sign in to open this binder.';

export const GUEST_BINDER_SIDEBAR_TITLE = 'Track this set';
export const GUEST_BINDER_SIDEBAR_BLURB =
  'Preview mode shows placeholders for missing copies. Sign in to mark what you own.';

// ── Scanner ──────────────────────────────────────────────────────────────────

/** Shown when a guest leaves the scanner. */
export const GUEST_SCAN_DISMISS_TITLE = 'Create an account to save scanned cards';
export const GUEST_SCAN_DISMISS_BLURB =
  'You can scan cards without an account. Create one to save what you scan to your collection and keep it on every device.';
export const GUEST_SCAN_DISMISS_CANCEL = 'Not now';

/** Shown when a guest chooses to add a scanned card. That choice is the save. */
export const GUEST_SCAN_ADD_TITLE = 'Create an account to add this card';
export const GUEST_SCAN_ADD_BLURB =
  'Adding a card saves it to your collection. Create an account to save it — nothing is stored while you browse as a guest.';
export const GUEST_SCAN_ADD_CANCEL = 'Keep scanning';

export const GUEST_SCAN_CREATE_ACCOUNT = 'Create account';

export function guestScanAddDescription(cardName?: string): string {
  if (!cardName) return GUEST_SCAN_ADD_BLURB;
  return `Adding ${cardName} saves it to your collection. Create an account to save it — nothing is stored while you browse as a guest.`;
}

// ── Play & matches ───────────────────────────────────────────────────────────

export const GUEST_MATCH_JOIN_TITLE = 'Sign in to join shared matches';
export const GUEST_MATCH_JOIN_BLURB =
  'Signed-in players can update shared scores and save results.';

export const GUEST_MATCH_SESSION_TITLE = 'Sign in to join this match';
export const GUEST_MATCH_SESSION_BLURB =
  'Only players in the match can update scores.';

export const GUEST_MATCH_HISTORY_TITLE = 'Sign in to see your matches';
export const GUEST_MATCH_HISTORY_BLURB =
  'Match history is private to your account.';

export const GUEST_MATCH_SAVE_RESULT = 'Sign in to save';

// ── Decks ────────────────────────────────────────────────────────────────────

export const GUEST_DECK_SAVE_TOAST_ACTION = 'save';
export const GUEST_DECK_PUBLISH_TOAST_ACTION = 'publish';
export const GUEST_DECK_REMIX_TOAST_ACTION = 'remix';

// ── Proxy / print ────────────────────────────────────────────────────────────

export const GUEST_PROXY_DOWNLOAD_TOAST_ACTION = 'download';

// ── Riftboundle ──────────────────────────────────────────────────────────────

export const GUEST_RIFTBOUNDLE_SAVE_STREAK = 'Sign in to save your streak';
export const GUEST_RIFTBOUNDLE_SAVE = GUEST_MATCH_SAVE_RESULT;
